// ─── White-label theming engine ───
// applyBrand(brand|null) composes the base palette for the current mode with the
// org's colours, recomputes the accent-derived tokens, mutates `V` in place (the
// whole app styles from V via inline styles), syncs the CSS vars the loader and
// global CSS read, updates the theme-color / status-bar metas, caches a splash
// subset in localStorage (read by the index.html bootstrap before React mounts)
// and notifies onThemeChange subscribers so the tree re-renders.
//
// Solo users: applyBrand(null) yields a V that deep-equals today's DARK_THEME /
// LIGHT_THEME (tests/brand.test.mjs asserts deriveTokens reproduces the literals).
import { V, DARK_THEME, LIGHT_THEME, syncCssVars, notifyThemeChange } from './theme';
import { LS } from './storage';
import { deriveTokens, composeBrand, onAccentFor } from './brandTokens';

export { deriveTokens } from './brandTokens';

export const DEFAULT_APP_NAME = "IRONLOG";
export const BRAND_CACHE_KEY = "ft-org-brand";
const MAX_LOGO_DATA = 20 * 1024 * 1.4; // 20 KB PNG as a base64 data URL

// Splash subset written for index.html's bootstrap script
export function brandCacheFrom(b) {
  if (!b) return null;
  const logo = typeof b.logo_small_data === "string" && b.logo_small_data.startsWith("data:image/") && b.logo_small_data.length <= MAX_LOGO_DATA ? b.logo_small_data : null;
  return {
    app_name: b.app_name || null, accent: b.accent || null, accent2: b.accent2 || null,
    bg_dark: b.bg_dark || null, bg_light: b.bg_light || null,
    accent_light: b.accent_light || null, accent2_light: b.accent2_light || null,
    on_accent: b.on_accent || null, on_accent_light: b.on_accent_light || null, logo_data: logo, version: b.version || null,
  };
}

export function applyBrand(brand) {
  const b = brand && typeof brand === "object" ? brand : null;
  const base = V.mode === "light" ? LIGHT_THEME : DARK_THEME;
  const merged = composeBrand(base, b);
  const derived = deriveTokens(merged);
  const onAccent = onAccentFor(merged, b);
  Object.assign(V, merged, derived, { brand: b, onAccent });
  if (typeof document !== "undefined") {
    document.body.style.background = V.bg;
    document.body.style.color = V.text;
    syncCssVars(V);
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", V.bg);
    const apple = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (apple) apple.setAttribute("content", V.mode === "light" ? "default" : "black-translucent");
  }
  if (b) LS.set(BRAND_CACHE_KEY, brandCacheFrom(b));
  else LS.del(BRAND_CACHE_KEY);
  notifyThemeChange(V.mode);
  return V;
}

export const appName = () => (V.brand && V.brand.app_name) || DEFAULT_APP_NAME;
export const brandLogoUrl = () => (V.brand && (V.brand.logo_url || V.brand.logo_small_data)) || null;
export const isBranded = () => !!(V.brand && V.brand.app_name);
